import {
  createRootRouteWithContext,
  Outlet,
  useRouterState,
  useNavigate,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import type { QueryClient } from '@tanstack/react-query';
import { AppShell } from '@/components/layout/AppShell';
import { NotFoundView, ErrorBoundaryView } from '@/components/errors';
import { useAuth } from '@/lib/auth';
import { useEffect } from 'react';

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFoundView,
  errorComponent: ({ error, reset }) => <ErrorBoundaryView error={error} reset={reset} />,
});

/** Routes that don't require authentication */
const PUBLIC_PATHS = ['/login', '/accept-invite'];

function isPublicPath(path: string): boolean {
  return PUBLIC_PATHS.includes(path);
}

function RootLayout() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const navigate = useNavigate();
  const { isAuthenticated, authResolved } = useAuth();

  // Route guard: redirect unauthenticated users to /login
  useEffect(() => {
    if (authResolved && !isAuthenticated && !isPublicPath(currentPath)) {
      navigate({ to: '/login' });
    }
  }, [authResolved, isAuthenticated, currentPath, navigate]);

  // Login pages have their own full-screen layout
  const isEntryPage =
    currentPath === '/' || currentPath === '/login' || currentPath === '/accept-invite';

  if (isEntryPage) {
    return (
      <>
        <Outlet />
        {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
      </>
    );
  }

  return (
    <AppShell>
      <Outlet />
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </AppShell>
  );
}
